import { memo, useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Tooltip } from '@mui/material'
import tw from 'twin.macro'
import { Button } from './Button'
import { GSIcon } from './GSIcon'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { DownloadStepDocument } from 'src/constants/actionTypes'
import { handleApiError } from 'src/utils/errorHandlers'
import {
  getMimeTypeFromExtension,
  downloadFileFromBuffer,
  downloadFileFromUrl,
} from 'src/utils/fileDownload'

interface Props {
  variant?: 'icon' | 'default'
  iconPosition?: 'left' | 'right'
  filePath?: string
  fileName?: string
  partnerDriverId?: string
  stepId?: string
  documentId?: string
  fileExtension?: string
}

const FileDownloadButtonComponent = ({
  variant = 'default',
  iconPosition,
  filePath,
  fileName,
  partnerDriverId,
  stepId,
  documentId,
  fileExtension: fileExtensionProp,
}: Props) => {
  const dispatch = useDispatch()
  const [isDownloading, setIsDownloading] = useState<boolean>(false)

  const handleDownload = useCallback(async () => {
    try {
      setIsDownloading(true)

      if (partnerDriverId && stepId && documentId) {
        const buffer: ArrayBuffer = await createAsyncAction(
          dispatch,
          DownloadStepDocument.request({
            partnerDriverId,
            stepId,
            documentId,
          }),
        )

        const fileExtension = fileExtensionProp || fileName?.split('.').pop() || 'pdf'
        const downloadFileName = fileName || `document.${fileExtension}`
        const mimeType = getMimeTypeFromExtension(fileExtension)

        downloadFileFromBuffer(buffer, downloadFileName, mimeType)
      } else if (filePath) {
        await downloadFileFromUrl(filePath, fileName)
      }
    } catch (error) {
      handleApiError(error)
    } finally {
      setIsDownloading(false)
    }
  }, [partnerDriverId, stepId, documentId, filePath, fileName, fileExtensionProp, dispatch])

  const shouldShowDownloadButton = (partnerDriverId && stepId && documentId) || Boolean(filePath)

  if (!shouldShowDownloadButton) {
    return null
  }

  const icon = <GSIcon iconName="gs_icon_circle_arrow_down" width={20} />
  const isIconVariant = variant === 'icon'
  const useLeftIcon = isIconVariant || iconPosition === 'left'

  const button = (
    <Button
      size="small"
      color={isIconVariant ? 'grey' : 'white'}
      iconOnly={isIconVariant}
      {...(useLeftIcon ? { leftIcon: icon } : { rightIcon: icon })}
      onClick={handleDownload}
      loading={isDownloading}
      className={isIconVariant ? tw`min-w-[36px]` : tw`min-w-[100px]`}
    >
      {!isIconVariant ? 'Download' : undefined}
    </Button>
  )

  if (isIconVariant) {
    return (
      <Tooltip title="Download file" placement="right">
        <span>{button}</span>
      </Tooltip>
    )
  }

  return button
}

export const FileDownloadButton = memo(FileDownloadButtonComponent)
